<template>
	<div>
		<div class="mb-4">
			<h3 class="text-xl md:text-2xl font-semibold text-gray-700">
				{{ __("Your price cockpit") }}
			</h3>
			<p class="text-gray-500">
				{{ __("At cropspot we track and collect prices all day so you no longer have to. This shows the development of the best non-binding free-on-farm daily prices for your home farm. That way you always have an overview of how the price level in your region is developing!") }}
			</p>
		</div>

		<div class="rounded-lg sm:rounded-xl p-4 lg:p-6 shadow bg-white">
			<div
				class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4"
			>
				<div>
					<h3 class="text-xl md:text-2xl font-semibold text-gray-700">
						{{ __("Free-on-farm daily price") }}
					</h3>
				</div>

				<div v-if="grades.length" class="flex flex-col sm:flex-row gap-3">
				<ui-select-group
					class="sm:w-56"
					v-model="selectedGradeId"
					field="product_grade_id"
					:label="__('Product')"
					:options="grades"
					:form="formStub"
					:required="true"
					:placeholder="false"
					v-on:change="loadSeries"
				/>

				<ui-select-group
					v-if="harvestYears.length"
					class="sm:w-40"
					v-model="selectedHarvestYear"
					field="product_harvest"
					:label="__('Harvest year')"
					:options="harvestYears"
					:form="formStub"
					:required="true"
					:placeholder="false"
					v-on:change="loadSeries"
				/>
			</div>
		</div>

			<div
				v-if="loading"
				key="chart-loading"
				class="h-96 flex items-center justify-center text-gray-500"
			>
				Wird geladen …
			</div>

			<div
				v-else-if="!series.length"
				key="chart-empty"
				class="h-96 flex items-center justify-center text-center text-gray-500 px-4"
			>
				<p v-html="emptyMessage"></p>
			</div>

			<div v-else key="chart-canvas" ref="chart" class="w-full h-96"></div>

			<!-- Bester Preis heute + Anfrage -->
			<div
				class="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
			>
				<p v-if="todayBest" class="text-gray-700">
					Bester Frei-Fuhre-Preis heute:
					<span class="font-semibold text-gray-900">{{ todayPriceFormatted }} €/t</span>
					<span v-if="todayTermFormatted" class="text-gray-500">({{ todayTermFormatted }})</span>
				</p>

				<p v-else class="text-gray-500">
					{{ __("There is no free-on-farm daily price available for today yet.") }}
				</p>

				<ui-tooltip :text="inquiryTooltip">
					<template v-slot:label>
						<ui-button
							class="px-5 py-2"
							:disabled="!canInquire"
							v-on:click="openInquiryModal"
						>
							{{ inquiryButtonLabel }}
						</ui-button>
					</template>
				</ui-tooltip>
			</div>
		</div>
	</div>
</template>

<script>
import axios from "axios";
import * as echarts from "echarts";
import { dateToString, formatLocalizedNumber } from "../../cropOptions";
import { formatDate } from "../../utils/date";

export default {
	name: "DashboardPriceChart",
	data() {
		return {
			grades: [],
			selectedGradeId: null,
			harvestYears: [],
			selectedHarvestYear: null,
			series: [],
			todayBest: null,
			radiusKm: null,
			loading: true,
			myChart: null,
			formStub: { hasErrors: false, errors: {} },
		};
	},
	mounted() {
		this.loadInitial();
		window.addEventListener("resize", this.onResize);
		this.$bus.$on("local_price_inquiry:created", this.loadSeries);
	},
	beforeDestroy() {
		window.removeEventListener("resize", this.onResize);
		this.$bus.$off("local_price_inquiry:created", this.loadSeries);
		this.disposeChart();
	},
	computed: {
		/**
		 * Empty-State-Text: unterscheidet, ob der Standort generell keine Preise hat
		 * (keine Produkte auswählbar) oder nur die gewählte Produkt/Erntejahr-Kombination leer ist.
		 */
		emptyMessage() {
			if (this.grades.length) {
				const grade = this.grades.find((g) => g.id === this.selectedGradeId);

				return this.__(
					"We unfortunately do not have enough historical daily prices for :product :harvest in your region to outline a meaningful price trend. If available, feel free to send us your past price offers to :email so that we can add them to our data set and consolidate them for you.",
					{
						args: {
							product: grade ? this.__(grade.name) : "",
							harvest: this.selectedHarvestYear ?? "",
							email:
								'<a class="font-semibold underline" href="mailto:info@cropspot.com">info@cropspot.com</a>',
						},
					}
				);
			}

			return this.__("No freight-adjusted prices are available for your location within :radius km yet.", {
				args: { radius: this.radiusKm },
			});
		},

		/**
		 * Heutiger Bestpreis als deutsch formatierter Betrag (Komma), oder null.
		 */
		todayPriceFormatted() {
			if (!this.todayBest) {
				return null;
			}

			return formatLocalizedNumber(this.todayBest.price);
		},

		/**
		 * Übliche Termin-Formulierung zum heutigen Bestpreis (z.B. "Im Monat November 26"),
		 * analog zum Tooltip der Zeitreihe. Leerer String, wenn keine Termindaten vorliegen.
		 */
		todayTermFormatted() {
			if (!this.todayBest) {
				return null;
			}

			return dateToString(
				{
					date: {
						type: this.todayBest.date_type,
						month_from: this.todayBest.date_month_from,
						month_to: this.todayBest.date_month_to,
					},
				},
				"de",
				true
			);
		},

		/**
		 * Ob aktuell eine Anfrage gestellt werden kann: es muss ein heutiger Bestpreis
		 * vorliegen und der Nutzer darf zu diesem Preis noch keine Anfrage gestellt haben.
		 */
		canInquire() {
			return !!this.todayBest && !this.todayBest.alreadyInquired;
		},

		/**
		 * Button-Beschriftung, situationsabhängig: signalisiert eine bereits gestellte Anfrage
		 * auch ohne Hover (sonst wären „bereits beboten" und „kein Preis" optisch identisch).
		 */
		inquiryButtonLabel() {
			if (this.todayBest && this.todayBest.alreadyInquired) {
				return this.__("Already inquired");
			}

			return this.__("Submit inquiry");
		},

		/**
		 * Tooltip-Text des Anfrage-Buttons, situationsabhängig — auch (und gerade) im
		 * deaktivierten Zustand, damit klar ist, warum keine Anfrage möglich ist.
		 */
		inquiryTooltip() {
			if (!this.todayBest) {
				return this.__("There is no free-on-farm daily price for today – an inquiry is currently not possible.");
			}

			if (this.todayBest.alreadyInquired) {
				return this.__("You have already submitted an inquiry at this price.");
			}

			return this.__("Submit a non-binding inquiry at today's best free-on-farm price.");
		},
	},
	methods: {
		/**
		 * Grades, Default-Auswahl und erste Zeitreihe laden.
		 *
		 * TODO: Fehlerzustand fehlt — schlägt der Request fehl, erscheint der Leer-Zustand
		 * („keine Preise", radiusKm = null) statt einer Fehlermeldung.
		 */
		loadInitial() {
			this.loading = true;

			axios
				.get(this.$route("local_prices.chart"))
				.then((response) => {
					this.grades = response.data.grades ?? [];
					this.selectedGradeId = response.data.selectedGradeId ?? null;
					this.harvestYears = response.data.harvestYears ?? [];
					this.selectedHarvestYear = response.data.selectedHarvestYear ?? null;
					this.radiusKm = response.data.radiusKm;
					this.series = response.data.series ?? [];
					this.todayBest = response.data.todayBest ?? null;
				})
				.finally(() => {
					this.loading = false;
					this.$nextTick(this.renderChart);
				});
		},

		/**
		 * Nur die Zeitreihe für das gewählte Produkt neu laden.
		 */
		loadSeries() {
			this.loading = true;

			axios
				.get(this.$route("local_prices.chart"), {
					params: {
						product_grade_id: this.selectedGradeId,
						product_harvest: this.selectedHarvestYear,
					},
				})
				.then((response) => {
					this.series = response.data.series ?? [];
					this.todayBest = response.data.todayBest ?? null;
				})
				.finally(() => {
					this.loading = false;
					this.$nextTick(this.renderChart);
				});
		},

		/**
		 * echarts-Instanz (neu) aufbauen bzw. mit aktuellen Daten befüllen.
		 */
		renderChart() {
			if (!this.series.length || !this.$refs.chart) {
				this.disposeChart();

				return;
			}

			const points = this.series.map((i) => ({
				value: [i.date, Number(i.price)],
				date: i.date,
				date_type: i.date_type,
				date_month_from: i.date_month_from,
				date_month_to: i.date_month_to,
			}));

			const option = {
				grid: {
					show: true,
					top: "16px",
					left: "4px",
					right: "2px",
					bottom: "5%",
					containLabel: true,
				},
				xAxis: {
					type: "time",
					axisLine: { show: true, lineStyle: { color: "#DFDFDE" } },
					axisLabel: { color: "#757575", formatter: "{MMM} {yy}" },
					splitLine: { show: true, lineStyle: { color: "#EBEBEA" } },
				},
				yAxis: {
					type: "value",
					scale: true,
					axisLine: { show: true, lineStyle: { color: "#DFDFDE" } },
					axisLabel: { color: "#757575", formatter: "{value} €/t" },
					splitLine: { show: true, lineStyle: { color: "#EBEBEA" } },
				},
				series: [
					{
						name: "Frei-Fuhre-Preis (bester)",
						type: "line",
						showSymbol: false,
						smooth: false,
						data: points,
						symbolSize: 4,
						itemStyle: {
							color: "#fff",
							borderColor: "#cade86",
							borderWidth: 2,
						},
						emphasis: {
							itemStyle: {
								color: "#fff",
								borderColor: "#cade86",
								borderWidth: 2,
							},
						},
						lineStyle: { width: 2, color: "#454545" },
						areaStyle: { color: "#aeb1b1" },
					},
				],
				textStyle: {
					fontFamily: "Barlow, Roboto, Helvetica, Arial, sans-serif",
					fontSize: "14",
					fontWeight: "500",
				},
				tooltip: {
					trigger: "axis",
					backgroundColor: "#fff",
					borderColor: "#DFDFDE",
					textStyle: { color: "#454545" },
					formatter: (params) => {
						const point = params[0];
						const date = formatDate(point.data.date, { locale: "de-DE" });
						const price = formatLocalizedNumber(point.value[1]);
						const term = dateToString(
							{
								date: {
									type: point.data.date_type,
									month_from: point.data.date_month_from,
									month_to: point.data.date_month_to,
								},
							},
							"de",
							true
						);

						return (
							date +
							`<div><span class="inline-block w-2.5 h-2.5 rounded-full mr-1 bg-[#cade86]"></span>${term}` +
							`<span class="float-right ml-5"><b>${price}&nbsp;&euro;/t</b></span></div>`
						);
					},
				},
			};

			// Der Chart-Container wird während des Ladens per v-if aus dem DOM genommen und
			// danach neu gerendert — die alte Instanz hinge sonst am entfernten Knoten. Daher
			// immer neu initialisieren.
			this.disposeChart();
			this.myChart = echarts.init(this.$refs.chart);
			this.myChart.setOption(option);
		},

		onResize() {
			this.myChart?.resize();
		},

		/**
		 * Anfrage-Modal für den heutigen Bestpreis öffnen. Die localPrice-Referenz
		 * steckt als Route-Param in der dataUrl; der Endpoint liefert Kontakt-Prefill/Optionen und
		 * echot die localPriceId zurück. Der angezeigte Preis geht bewusst als data mit — er wird
		 * unverändert übernommen und darf nicht serverseitig neu berechnet werden.
		 */
		openInquiryModal() {
			if (!this.canInquire) {
				return;
			}

			this.$bus.$emit("modal:open", {
				view: {
					view: "views/LocalPriceInquiry/Form",
					dataUrl: this.$route("local_price_inquiries.dependencies", {
						id: this.todayBest.localPriceId,
					}),
					data: {
						price: this.todayBest.price,
					},
				},
			});
		},

		disposeChart() {
			if (this.myChart) {
				this.myChart.dispose();
				this.myChart = null;
			}
		},
	},
};
</script>
